// Typography.tsx - Polymorphic Typography component using styleProps system
import React, { forwardRef } from "react";
import Box from "../Box/Box";
import { wishUITheme } from "../../theme/theme";
import { TypographyBaseProps, TypographyComponent } from "./Typography.types";

/**
 * Typography component - A polymorphic text component
 *
 * This is essentially a specialized Box component with better defaults for text content.
 * All typography styling comes directly from the styleProps system.
 *
 * @example
 * // Basic paragraph
 * <Typography>This is a paragraph</Typography>
 *
 * // Custom styling
 * <Typography fontSize={18} fontWeight={600} color="blue.500">
 *   Custom styled text
 * </Typography>
 *
 * // As a heading
 * <Typography as="h2" fontSize={24} fontWeight="bold">
 *   This is a heading
 * </Typography>
 *
 * // Using secondary font
 * <Typography fontFamily="secondary">
 *   This text uses the Lexend font family
 * </Typography>
 *
 * // Truncated text
 * <Typography textOverflow="ellipsis" lineClamp={2}>
 *   This text will be truncated after two lines with an ellipsis...
 * </Typography>
 */
export const Typography = forwardRef(function Typography(
  props: TypographyBaseProps & { as?: React.ElementType },
  ref
) {
  const {
    as = "span",
    fontFamily = "primary",
    fontSize = 16,
    fontWeight = 400,
    lineHeight = 24,
    textAlign = "left",
    ...rest
  } = props;

  // Resolve font family from theme if it's a known key
  let resolvedFontFamily: string;

  // First try to get from theme if it's a known key
  if (fontFamily in wishUITheme.typography.fontFamilies) {
    resolvedFontFamily =
      wishUITheme.typography.fontFamilies[
        fontFamily as keyof typeof wishUITheme.typography.fontFamilies
      ];
  } else {
    // Otherwise use the provided value with fallbacks
    resolvedFontFamily = `${fontFamily}, ${wishUITheme.typography.fontFamilies.primary}, ${wishUITheme.typography.fontFamilies.fallback}`;
  }

  return (
    <Box
      ref={ref}
      as={as}
      fontFamily={resolvedFontFamily}
      fontSize={fontSize}
      fontWeight={fontWeight}
      lineHeight={lineHeight}
      textAlign={textAlign}
      {...(rest as Record<string, unknown>)}
      m={0}
      p={0}
    />
  );
}) as TypographyComponent;

// Set display name for debugging
Typography.displayName = "Typography";

export default Typography;
